/**
 * One safety recommendation, in full: what was asked, of whom, where it stands and why, every
 * letter behind that, the ISAM reports from the investigations tied to it, and the recommendations
 * around it. A chat sits over the lot, seeing this recommendation alone.
 *
 * The tab is opened with whatever the opener had (a search row, or a reference from another
 * recommendation), so it reads only the id from that and fetches the rest itself.
 */

import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Fab,
    IconButton,
    Link,
    Paper,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import {
    SafetyRecommendationDTO,
    SafetyRecommendationLetterDTO,
    SafetyRecommendationLinkedReportDTO,
    SafetyRecommendationRefDTO,
    SafetyRecommendationRelatedDTO,
} from '../../../../../@types/safety-recommendation';
import {ResourceType} from '../../../../../@types/resource-type';
import {useWorkspaceContext} from '../../../../../context/workspaceContext';
import {
    useDetailChatContextSent,
    useDetailChatHistory,
    useRegisterStore,
} from '../../../../../stores/safety-recommendation-register.store';
import {PersistedChatMessage} from '../../../../../stores/irp-workbook.store';
import LLMChatDialog, {ChatMessage} from '../../../../common/LLMChatDialog';
import {RecommendationStatusChip} from '../../../../safety-recommendations/RecommendationOverview';
import {CATEGORY_COLOUR} from '../../../../safety-recommendations/RecommendationSummary';
import CorrespondenceList from '../../../../safety-recommendations/CorrespondenceList';
import safetyRecommendationService from '../../../../../services/safety-recommendation.service';
import {ageLabel, displayStatus, focusStatus} from '../../../../safety-recommendations/recommendations';
import {formatRecommendationContext} from '../../../../safety-recommendations/recommendationChatContext';

const SUGGESTED_PROMPTS = [
    'Summarise how this recommendation got to where it stands.',
    'What exactly is the NTSB asking the FAA to do, and what has the FAA offered instead?',
    'What would it take for the NTSB to close this acceptably?',
];

interface SafetyRecommendationEditorProps {
    /** Whatever the opener had: a search row, or a reference from another recommendation. */
    recommendation: SafetyRecommendationDTO | SafetyRecommendationRefDTO;
}

/** A section with a heading, shown whether or not its contents loaded. */
const Section: React.FC<{title: string; children: React.ReactNode}> = ({title, children}) => (
    <Box>
        <Typography variant="subtitle2" gutterBottom>{title}</Typography>
        {children}
    </Box>
);

const SafetyRecommendationEditor: React.FC<SafetyRecommendationEditorProps> = ({recommendation}) => {
    const id = recommendation.id;
    const {setCurrentWorkspaceObject} = useWorkspaceContext();
    const chatHistory = useDetailChatHistory(id);
    const chatContextSent = useDetailChatContextSent(id);
    const setChatHistory = useRegisterStore((state) => state.setDetailChatHistory);
    const setChatContextSent = useRegisterStore((state) => state.setDetailChatContextSent);

    const [rec, setRec] = useState<SafetyRecommendationDTO | null>(null);
    const [recError, setRecError] = useState<string | null>(null);
    const [letters, setLetters] = useState<SafetyRecommendationLetterDTO[] | null>(null);
    const [lettersError, setLettersError] = useState<string | null>(null);
    const [reports, setReports] = useState<SafetyRecommendationLinkedReportDTO[] | null>(null);
    const [reportsError, setReportsError] = useState<string | null>(null);
    const [related, setRelated] = useState<SafetyRecommendationRelatedDTO | null>(null);
    const [relatedError, setRelatedError] = useState<string | null>(null);
    const [chatOpen, setChatOpen] = useState(false);
    const today = useMemo((): Date => new Date(), []);

    // Four fetches, each with its own state: one failing leaves the rest on screen. A tab reopened
    // on another recommendation starts again, and answers for the old one are dropped.
    const showing = useRef(id);
    useEffect(() => {
        showing.current = id;
        setRec(null);
        setRecError(null);
        setLetters(null);
        setLettersError(null);
        setReports(null);
        setReportsError(null);
        setRelated(null);
        setRelatedError(null);
        const mine = (): boolean => showing.current === id;
        safetyRecommendationService.get(id)
            .then((loaded): void => { if (mine()) setRec(loaded); })
            .catch((failure: Error): void => { if (mine()) setRecError(failure.message); });
        safetyRecommendationService.getCorrespondence({id, number: recommendation.number})
            .then((loaded): void => { if (mine()) setLetters(loaded); })
            .catch((failure: Error): void => { if (mine()) setLettersError(failure.message); });
        safetyRecommendationService.getReports(id)
            .then((loaded): void => { if (mine()) setReports(loaded); })
            .catch((failure: Error): void => { if (mine()) setReportsError(failure.message); });
        safetyRecommendationService.getRelated(id)
            .then((loaded): void => { if (mine()) setRelated(loaded); })
            .catch((failure: Error): void => { if (mine()) setRelatedError(failure.message); });
    }, [id, recommendation.number]);

    const openReport = (report: SafetyRecommendationLinkedReportDTO): void => setCurrentWorkspaceObject({
        objectType: ResourceType.NEW_OCCURRENCE_REPORTS,
        object: {id: report.reportId, uniqueId: report.uniqueId},
    });

    const openRecommendation = (other: SafetyRecommendationRefDTO): void =>
        setCurrentWorkspaceObject({objectType: ResourceType.SAFETY_RECOMMENDATION, object: other});

    const contextPrepend = useMemo((): string => {
        if (!rec) return '';
        return `${formatRecommendationContext({rec, letters: letters ?? [], reports: reports ?? [], today})}

---

User Question: `;
    }, [rec, letters, reports, today]);

    const relatives = (title: string, refs: SafetyRecommendationRefDTO[]): React.ReactElement | null => (
        refs.length === 0 ? null : (
            <Box>
                <Typography variant="caption" color="text.secondary">{title}</Typography>
                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{mt: 0.5}}>
                    {refs.map((other) => (
                        <Tooltip key={other.id} title={displayStatus(other.status) || 'Status unknown'}>
                            <Chip
                                size="small"
                                clickable
                                label={other.number}
                                aria-label={`${other.number}, ${displayStatus(other.status) || 'status unknown'}`}
                                onClick={(): void => openRecommendation(other)}
                            />
                        </Tooltip>
                    ))}
                </Stack>
            </Box>
        )
    );

    if (recError) {
        return <Box sx={{p: 3}}><Typography color="error">{recError}</Typography></Box>;
    }
    if (!rec) {
        return <Box sx={{p: 3, display: 'flex', justifyContent: 'center'}}><CircularProgress size={28} /></Box>;
    }

    const nothingRelated = related !== null
        && related.supersededBy.length === 0 && related.supersedes.length === 0 && related.sameCase.length === 0;

    return (
        // The outer box anchors the floating chat and its button; the inner one scrolls
        <Box sx={{position: 'relative', height: '100%', minHeight: 0}}>
            <Box sx={{height: '100%', overflowY: 'auto', boxSizing: 'border-box', p: 2, display: 'flex', flexDirection: 'column', gap: 2}}>
                <Box>
                    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="h6" component="h1">{`${rec.source} Safety Recommendation ${rec.number}`}</Typography>
                        <RecommendationStatusChip
                            group={rec.focusStatusGroup}
                            label={displayStatus(focusStatus(rec)) || 'Status unknown'}
                        />
                        {rec.priority === 'CLASS I' && <Chip size="small" color="error" label="Urgent" />}
                        {rec.superseded && (
                            <Chip size="small" variant="outlined"
                                label={`Superseded by ${rec.supersededBy.map((s) => s.number).join(', ')}`} />
                        )}
                    </Stack>
                    {/* What kind of fix this asks for, grouped by category. The issuer's own coding is
                    filled and ours is outlined, and the tooltip names the source either way. */}
                    {(rec.taxonomyTags ?? []).length > 0 && (
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{mt: 1}}>
                            <Typography variant="caption" color="text.secondary">Taxonomy:</Typography>
                            {Array.from(new Map((rec.taxonomyTags ?? []).map((tag) => [tag.leafId, tag])).values())
                                .map((tag) => {
                                    const origins = (rec.taxonomyTags ?? [])
                                        .filter((each) => each.leafId === tag.leafId)
                                        .map((each) => each.origin);
                                    const fromIssuer = origins.includes('NTSB');
                                    const source = fromIssuer && origins.includes('MODEL')
                                        ? 'Tagged by the NTSB, and by ISAM'
                                        : fromIssuer ? 'Tagged by the NTSB' : 'Tagged by ISAM';
                                    return (
                                        <Tooltip describeChild key={tag.leafId} title={source}>
                                            <Chip
                                                size="small"
                                                color={CATEGORY_COLOUR[tag.category] ?? 'default'}
                                                variant={fromIssuer ? 'filled' : 'outlined'}
                                                label={`${tag.category} / ${tag.leaf}`}
                                            />
                                        </Tooltip>
                                    );
                                })}
                        </Stack>
                    )}
                    <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" sx={{mt: 0.5}}>
                        <Typography variant="caption" color="text.secondary">
                            {rec.issuedDate ? `Issued ${rec.issuedDate}` : 'Issue date unknown'}
                        </Typography>
                        {ageLabel(rec, today) && (
                            <Typography variant="caption" color="text.secondary">{ageLabel(rec, today)}</Typography>
                        )}
                        {rec.letterLink && (
                            <Link variant="caption" href={rec.letterLink} target="_blank" rel="noopener noreferrer">
                                Recommendation letter (PDF)
                            </Link>
                        )}
                        {rec.caseLink && (
                            <Link variant="caption" href={rec.caseLink} target="_blank" rel="noopener noreferrer">
                                {`Investigation page${rec.caseReportNumber ? ` (${rec.caseReportNumber})` : ''}`}
                            </Link>
                        )}
                    </Stack>
                </Box>

                <Typography variant="body1" sx={{whiteSpace: 'pre-line'}}>{rec.text}</Typography>

                {rec.assessmentExcerpt && (
                    <Alert severity="info" icon={false}>
                        <Typography variant="body2">
                            <Box component="span" sx={{fontWeight: 600}}>{`Why it stands here, NTSB ${rec.assessmentDate}: `}</Box>
                            {rec.assessmentExcerpt}
                        </Typography>
                    </Alert>
                )}

                {rec.otherAddressees.length > 0 && (
                    <Section title="Other addressees">
                        {rec.otherAddressees.map((addressee) => (
                            <Typography key={addressee.name} variant="body2">
                                {`${addressee.name}: ${displayStatus(addressee.status) || 'status unknown'}`}
                                {addressee.closedDate ? ` (closed ${addressee.closedDate})` : ''}
                            </Typography>
                        ))}
                    </Section>
                )}

                {(rec.caseSummary || rec.caseProbableCause) && (
                    <Section title={`Investigation ${rec.issuingCaseNumber || ''}`.trim()}>
                        {rec.caseSummary && <Typography variant="body2">{rec.caseSummary}</Typography>}
                        {rec.caseProbableCause && (
                            <Typography variant="body2" sx={{mt: 0.5}}>
                                <strong>Probable cause: </strong>
                                {rec.caseProbableCause}
                            </Typography>
                        )}
                    </Section>
                )}

                <Section title="ISAM reports from the investigations tied to it">
                    {reportsError && <Alert severity="error">{reportsError}</Alert>}
                    {!reportsError && reports === null && <CircularProgress size={20} />}
                    {reports !== null && reports.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                            No ISAM report comes from this investigation, or none you can see.
                        </Typography>
                    )}
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                        {(reports ?? []).map((report) => (
                            <Tooltip
                                key={`${report.reportId}-${report.relation}`}
                                title={report.relation === 'REITERATED'
                                    ? 'This later accident\'s investigation reiterated the recommendation'
                                    : 'This report\'s investigation issued the recommendation'}
                            >
                                <Chip
                                    size="small"
                                    clickable
                                    variant={report.relation === 'REITERATED' ? 'outlined' : 'filled'}
                                    color={report.relation === 'REITERATED' ? 'secondary' : 'default'}
                                    label={report.uniqueId}
                                    aria-label={`${report.uniqueId}, ${report.relation === 'REITERATED'
                                        ? 'reiterated the recommendation' : 'issued the recommendation'}`}
                                    onClick={(): void => openReport(report)}
                                />
                            </Tooltip>
                        ))}
                    </Stack>
                </Section>

                <Section title="Related recommendations">
                    {relatedError && <Alert severity="error">{relatedError}</Alert>}
                    {!relatedError && related === null && <CircularProgress size={20} />}
                    {nothingRelated && (
                        <Typography variant="body2" color="text.secondary">
                            Nothing supersedes this recommendation, it supersedes nothing, and its investigation issued no other.
                        </Typography>
                    )}
                    {related !== null && (
                        <Stack spacing={1}>
                            {relatives('Superseded by', related.supersededBy)}
                            {relatives('Supersedes', related.supersedes)}
                            {relatives('Issued by the same investigation', related.sameCase)}
                        </Stack>
                    )}
                </Section>

                <Section title="Correspondence">
                    <CorrespondenceList letters={letters} error={lettersError} group={rec.focusStatusGroup} />
                </Section>
            </Box>

            {!chatOpen && (
                <Fab
                    variant="extended"
                    color="primary"
                    size="medium"
                    onClick={(): void => setChatOpen(true)}
                    sx={{position: 'absolute', right: 24, bottom: 24, zIndex: 4}}
                >
                    <AutoAwesomeIcon sx={{mr: 1}} />
                    Ask about this one
                </Fab>
            )}

            {/* Kept mounted while hidden so an answer in progress still lands. */}
            <Paper
                elevation={8}
                role="dialog"
                aria-label="Ask about this recommendation"
                data-testid="recommendation-detail-chat-panel"
                sx={{
                    display: chatOpen ? 'flex' : 'none',
                    flexDirection: 'column',
                    position: 'absolute',
                    right: 24,
                    bottom: 24,
                    width: 'min(480px, calc(100% - 48px))',
                    height: 'min(640px, calc(100% - 48px))',
                    zIndex: 5,
                    overflow: 'hidden',
                }}
            >
                <LLMChatDialog
                    title={`Ask about ${rec.number}`}
                    contextPrepend={contextPrepend}
                    contextMessage={`Answers cover ${rec.number}, its correspondence and the reports tied to it.`}
                    fillHeight
                    headerIconsPaddingRight={4}
                    headerActions={
                        <IconButton size="small" aria-label="Close the conversation" onClick={(): void => setChatOpen(false)}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    }
                    suggestedPrompts={SUGGESTED_PROMPTS}
                    messages={chatHistory as ChatMessage[]}
                    onMessagesChange={(messages: ChatMessage[]): void => setChatHistory(id, messages as PersistedChatMessage[])}
                    contextSent={chatContextSent}
                    onContextSentChange={(sent: boolean): void => setChatContextSent(id, sent)}
                />
            </Paper>
        </Box>
    );
};

export default SafetyRecommendationEditor;
